import { useChangeFilterTrack } from "@/_old/features/analytics";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { Label } from "@/core/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Space } from "./types";

interface SpaceInputProps extends React.ComponentPropsWithoutRef<
  typeof Popover
> {
  location: string;
  data: Space[];
  isLoading: boolean;
  selectedCity: { id: string; name: string }[] | null;
  onSubmit?: boolean;
  onChange?: (value: Space[] | null) => void;
}

export function SpaceInput({
  location,
  data,
  isLoading,
  selectedCity,
  onSubmit,
  onChange,
  ...props
}: SpaceInputProps) {
  const { t } = useTranslation(["dashboard"]);
  const [filteredData, setFilteredData] = useState<Space[]>([]);
  const { onChangeFilter } = useChangeFilterTrack();
  const { filters, setFilters } = useDashboardFilters();
  const [selectedItem, setSelectedItem] = useState<Space[] | null>(null);

  const handleChange = useCallback(
    (value: Space[] | null) => {
      //   onChangeFilter("FILTER_MEMBERS", eventCategory);

      if (onSubmit) {
        onChange?.(value as Space[]);
      } else {
        setFilters({ spaces: value as Space[] });
      }
    },
    [onChange, onSubmit, setFilters],
  );

  const handleSelect = useCallback(
    (space: Space | null) => {
      if (space === null) {
        setSelectedItem(null);
        handleChange(null);
      } else {
        setSelectedItem((prev) => {
          if (!prev) {
            const newSelection = [space];
            handleChange(newSelection);
            return newSelection;
          }

          const isAlreadySelected = prev.some(
            (selectedSpace) => selectedSpace.id === space.id,
          );

          if (isAlreadySelected) {
            const newSelection = prev.filter(
              (selectedSpace) => selectedSpace.id !== space.id,
            );
            handleChange(newSelection.length > 0 ? newSelection : null);
            return newSelection.length > 0 ? newSelection : null;
          } else {
            const newSelection = [...prev, space];
            handleChange(newSelection);
            return newSelection;
          }
        });
      }
    },
    [handleChange],
  );

  useEffect(() => {
    setSelectedItem(filters.spaces || null);
  }, [filters.spaces]);

  useEffect(() => {
    if (selectedCity && selectedCity.length > 0) {
      setFilteredData(
        data.filter((space) =>
          selectedCity?.some((city) => city.id === space.city),
        ),
      );
    } else {
      setFilteredData(data);
    }
  }, [selectedCity, data]);

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${location}-space-filter`}>
        {t("space-filter-label") as string}
      </Label>

      <Popover {...props}>
        <PopoverTrigger asChild>
          <Button
            id={`${location}-space-filter`}
            variant="outline"
            disabled={isLoading}
            className="justify-between w-full bg-white"
          >
            <span className="overflow-hidden font-normal text-ellipsis">
              {selectedItem && selectedItem.length > 0
                ? selectedItem.length === 1
                  ? `${selectedItem[0].name}`
                  : (t("space-filter-selected-multiple", {
                      count: selectedItem.length,
                    }) as string)
                : (t("space-filter-placeholder") as string)}
            </span>
            <ChevronDown className="opacity-50 size-4" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[500px] max-w-[500px]">
          <Command>
            <CommandInput
              placeholder={t("space-filter-search-placeholder") as string}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>{t("space-filter-empty-state")}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value=""
                  onSelect={() => {
                    handleSelect(null);
                  }}
                >
                  <Checkbox
                    className="mr-2"
                    checked={!selectedItem || selectedItem.length === 0}
                  />

                  {t("space-filter-placeholder")}
                </CommandItem>
                {filteredData.map((space, index) => (
                  <CommandItem
                    key={`${space.id}-${index}`}
                    value={space.name}
                    onSelect={() => {
                      handleSelect(space);
                    }}
                  >
                    <Checkbox
                      className="mr-2"
                      checked={
                        selectedItem?.some(
                          (selectedMember) => selectedMember.id === space.id,
                        ) ||
                        selectedItem?.length === 0 ||
                        !selectedItem
                      }
                    />

                    {space.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
